import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  query,
  setDoc,
  Timestamp,
  updateDoc,
  where
} from 'firebase/firestore';
import { getAuth, sendPasswordResetEmail } from 'firebase/auth';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faBriefcase,
  faCalendarDay,
  faCircleInfo,
  faCoins,
  faEnvelope,
  faMoneyCheckDollar,
  faPhone,
  faUser
} from '@fortawesome/free-solid-svg-icons';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import Table from 'react-bootstrap/Table';
import swal from 'sweetalert';
import { app } from '../../firebase';
import '../dash.css';
import {
  formatDateTime,
  formatKes,
  formatShortDate,
  getDateTimeInputValue,
  getMonthKey,
  getMonthLabel
} from '../../utils/financeWorkflow';

function ViewEmployeeComp() {
  const { id } = useParams();
  const navigate = useNavigate();
  const firestore = getFirestore(app);
  const auth = getAuth();

  const [employee, setEmployee] = useState(null);
  const [salaryPayments, setSalaryPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [periodKey, setPeriodKey] = useState(getMonthKey());
  const [formState, setFormState] = useState({
    amount: '',
    paymentDate: getDateTimeInputValue(),
    method: 'Bank Transfer',
    reference: '',
    notes: ''
  });

  const loadEmployeePage = useCallback(async () => {
    if (!id) {
      setEmployee(null);
      setSalaryPayments([]);
      return;
    }

    const [employeeSnap, paymentsSnap] = await Promise.all([
      getDoc(doc(firestore, 'Employees', id)),
      getDocs(query(collection(firestore, 'SalaryPayments'), where('employeeId', '==', id)))
    ]);

    if (!employeeSnap.exists()) {
      setEmployee(null);
      setSalaryPayments([]);
      return;
    }

    setEmployee({ id: employeeSnap.id, ...employeeSnap.data() });

    const payments = paymentsSnap.docs
      .map((item) => ({ id: item.id, ...item.data() }))
      .sort((first, second) => {
        const firstTime = first.paymentDate?.seconds || first.createdAt?.seconds || 0;
        const secondTime = second.paymentDate?.seconds || second.createdAt?.seconds || 0;
        return secondTime - firstTime;
      });
    setSalaryPayments(payments);
  }, [firestore, id]);

  useEffect(() => {
    const fetchPage = async () => {
      try {
        setLoading(true);
        await loadEmployeePage();
      } catch (error) {
        console.error('Failed to load employee profile:', error);
        swal('Error', 'Employee details could not be loaded right now.', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchPage();
  }, [loadEmployeePage]);

  const salaryAmount = Number(employee?.salary || 0);
  const currentPeriodLabel = getMonthLabel(periodKey);
  const periodPayments = useMemo(() => {
    return salaryPayments.filter((payment) => {
      const paymentPeriodKey = payment.salaryPeriodKey || getMonthKey(payment.paymentDate);
      return paymentPeriodKey === periodKey;
    });
  }, [periodKey, salaryPayments]);

  const periodPaid = periodPayments.reduce((sum, payment) => sum + Number(payment.amount || 0), 0);
  const remainingSalary = Math.max(salaryAmount - periodPaid, 0);
  const allTimePaid = salaryPayments.reduce((sum, payment) => sum + Number(payment.amount || 0), 0);
  const salaryProgress = salaryAmount > 0 ? Math.min((periodPaid / salaryAmount) * 100, 100) : 0;
  const paymentTone = remainingSalary <= 0 && salaryAmount > 0 ? 'success' : periodPaid > 0 ? 'warning' : 'danger';
  const paymentStatus =
    salaryAmount <= 0 ? 'Amount not set' : remainingSalary <= 0 ? 'Settled' : periodPaid > 0 ? 'Partially paid' : 'Not paid';

  const copyToClipboard = async (value, label) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      swal('Copied', `${label} copied to clipboard.`, 'success');
    } catch (error) {
      console.error('Clipboard copy failed:', error);
    }
  };

  const handleToggleStatus = async () => {
    if (!employee?.id) return;
    const currentStatus = employee.employeeStatus || 'Active';
    const nextStatus = currentStatus === 'Active' ? 'Inactive' : 'Active';

    setUpdatingStatus(true);
    try {
      await updateDoc(doc(firestore, 'Employees', employee.id), {
        employeeStatus: nextStatus
      });
      setEmployee((currentEmployee) => (
        currentEmployee ? { ...currentEmployee, employeeStatus: nextStatus } : currentEmployee
      ));
      swal('Updated', `Employee status set to ${nextStatus}.`, 'success');
    } catch (error) {
      console.error('Failed to update employee status:', error);
      swal('Error', 'Failed to update employee status.', 'error');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleResetPassword = async () => {
    if (!employee?.employeeEmail) {
      swal('Missing Email', 'This employee has no email on record.', 'error');
      return;
    }

    try {
      await sendPasswordResetEmail(auth, employee.employeeEmail);
      swal('Sent', 'Password reset email sent successfully.', 'success');
    } catch (error) {
      console.error('Failed to send reset email:', error);
      swal('Error', 'Could not send password reset email.', 'error');
    }
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setFormState((currentForm) => ({
      ...currentForm,
      [name]: value
    }));
  };

  const handlePaySalary = async () => {
    if (!employee?.id) {
      return;
    }

    if (!auth.currentUser) {
      swal('Error', 'You need to be signed in to record salary payments.', 'error');
      return;
    }

    const amountValue = Number(formState.amount);
    if (!Number.isFinite(amountValue) || amountValue <= 0) {
      swal('Invalid amount', 'Payment amount should be greater than zero.', 'warning');
      return;
    }

    if (amountValue > remainingSalary) {
      swal('Amount too high', `You can only pay up to ${formatKes(remainingSalary)} for ${currentPeriodLabel}.`, 'warning');
      return;
    }

    const paymentDateValue = new Date(formState.paymentDate);
    if (Number.isNaN(paymentDateValue.getTime())) {
      swal('Invalid date', 'Please choose a valid payment date.', 'warning');
      return;
    }

    setSubmitting(true);
    try {
      const salaryPaymentRef = doc(collection(firestore, 'SalaryPayments'));
      await setDoc(salaryPaymentRef, {
        employeeId: employee.id,
        employeeName: employee.employeeName || '',
        campus: employee.campus || '',
        jobPosition: employee.jobPosition || '',
        employeeStatus: employee.employeeStatus || 'Active',
        amount: amountValue,
        paymentDate: Timestamp.fromDate(paymentDateValue),
        method: formState.method,
        reference: formState.reference.trim(),
        notes: formState.notes.trim(),
        salaryPeriodKey: periodKey,
        salaryPeriodLabel: currentPeriodLabel,
        expectedSalary: salaryAmount,
        createdAt: Timestamp.now(),
        createdBy: auth.currentUser?.uid || '',
        docId: salaryPaymentRef.id
      });

      swal('Success', 'Salary payment recorded successfully.', 'success');
      setFormState({
        amount: '',
        paymentDate: getDateTimeInputValue(),
        method: 'Bank Transfer',
        reference: '',
        notes: ''
      });
      await loadEmployeePage();
    } catch (error) {
      console.error('Failed to record salary payment:', error);
      swal('Error', 'Failed to record salary payment.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="teamsComponent opsWorkspace">
        <div className="opsLoadingCard">
          <Spinner animation="border" role="status" />
          <span>Loading employee...</span>
        </div>
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="teamsComponent opsWorkspace">
        <div className="opsEmptyState">
          <h6>Employee not found</h6>
          <p>The employee profile you are looking for could not be found.</p>
          <button type="button" className="opsGhostBtn" onClick={() => navigate('/employees')}>
            <FontAwesomeIcon icon={faArrowLeft} /> Back to employees
          </button>
        </div>
      </div>
    );
  }
  return (
    <div className="teamsComponent opsWorkspace">
      <section className="opsHero">
        <div className="opsHeroCopy">
          <span className="opsEyebrow">Employee profile</span>
          <h3>{employee.employeeName || 'Employee'}</h3>
          <div className="opsHeroMeta">
            <span>{employee.jobPosition || 'Role not set'}</span>
            <span>{employee.campus || 'Campus not set'}</span>
            <span>{employee.employeeEmail || 'Email not set'}</span>
          </div>
        </div>
        <div className="opsHeroActions">
          <button type="button" className="opsGhostBtn" onClick={() => navigate('/pay-salaries')}>
            <FontAwesomeIcon icon={faArrowLeft} /> Back
          </button>
          <button type="button" className="opsGhostBtn" onClick={handleResetPassword}>
            Reset Password
          </button>
          <button type="button" className="opsGhostBtn" onClick={handleToggleStatus} disabled={updatingStatus}>
            {employee.employeeStatus === 'Active' ? 'Deactivate' : 'Activate'}
          </button>
        </div>
      </section>

      <section className="opsStatsGrid">
        <article className="opsStatCard">
          <span>{currentPeriodLabel} salary</span>
          <strong>{formatKes(salaryAmount)}</strong>
        </article>
        <article className="opsStatCard">
          <span>Paid this period</span>
          <strong>{formatKes(periodPaid)}</strong>
        </article>
        <article className="opsStatCard">
          <span>Remaining this period</span>
          <strong>{formatKes(remainingSalary)}</strong>
        </article>
        <article className="opsStatCard">
          <span>All-time paid</span>
          <strong>{formatKes(allTimePaid)}</strong>
        </article>
      </section>

      <section className="opsLayout">
        <article className="opsPanel opsPanel--wide">
          <div className="opsToolbar">
            <div>
              <h4>Salary history</h4>
              <p>Review the employee summary and every salary payment recorded below.</p>
            </div>
            <div className="opsFilters">
              <Form.Control type="month" value={periodKey} onChange={(event) => setPeriodKey(event.target.value)} />
            </div>
          </div>

          <div className="opsCoverageCard">
            <div>
              <span>Payment status</span>
              <strong>{paymentStatus}</strong>
            </div>
            <span className={`opsStatusPill opsStatusPill--${paymentTone}`}>{paymentStatus}</span>
          </div>
          <div className="employeeSalaryBar opsProgressBar">
            <span style={{ width: `${salaryProgress}%` }}></span>
          </div>

          <div className="opsInfoGrid opsInfoGrid--wide">
            <div className="opsInfoCard">
              <span><FontAwesomeIcon icon={faBriefcase} /> Engagement</span>
              <strong>{employee.engagementType || 'Not set'}</strong>
            </div>
            <div className="opsInfoCard">
              <span><FontAwesomeIcon icon={faCircleInfo} /> Tax type</span>
              <strong>{employee.taxType || 'Not set'}</strong>
            </div>
            <div className="opsInfoCard">
              <span><FontAwesomeIcon icon={faCalendarDay} /> Start date</span>
              <strong>{formatShortDate(employee.employeeStartDate, 'Not set')}</strong>
            </div>
            <div className="opsInfoCard">
              <span><FontAwesomeIcon icon={faUser} /> Status</span>
              <strong>{employee.employeeStatus || 'Active'}</strong>
            </div>
          </div>

          <div className="appsTableCard opsHistoryCard">
            <div className="appsTableHeader">
              <h6>Salary payments</h6>
              <span>{salaryPayments.length} records</span>
            </div>
            <div className="appsTableWrap">
              <Table hover responsive className="appsTable">
                <thead>
                  <tr>
                    <th>Payment date</th>
                    <th>Applied month</th>
                    <th>Amount</th>
                    <th>Method</th>
                    <th>Reference</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {salaryPayments.map((payment) => (
                    <tr key={payment.id || payment.createdAt?.seconds}>
                      <td>{formatDateTime(payment.paymentDate)}</td>
                      <td>{payment.salaryPeriodLabel || getMonthLabel(payment.salaryPeriodKey || getMonthKey(payment.paymentDate))}</td>
                      <td>{formatKes(payment.amount)}</td>
                      <td>{payment.method || '—'}</td>
                      <td>{payment.reference || '—'}</td>
                      <td>{payment.notes || '—'}</td>
                    </tr>
                  ))}
                  {salaryPayments.length === 0 && (
                    <tr>
                      <td colSpan="6" className="appsEmptyState">
                        No salary payments recorded yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </div>
        </article>

        <aside className="opsPanel">
          <div className="opsSectionHeader">
            <div className="opsSectionIcon">
              <FontAwesomeIcon icon={faMoneyCheckDollar} />
            </div>
            <div>
              <h4>Record salary payment</h4>
              <p>Pay this employee directly from their page and keep the selected month accurate.</p>
            </div>
          </div>

          <div className="opsInfoGrid">
            <div className="opsInfoCard">
              <span><FontAwesomeIcon icon={faCoins} /> Salary</span>
              <strong>{formatKes(salaryAmount)}</strong>
            </div>
            <div className="opsInfoCard">
              <span><FontAwesomeIcon icon={faCalendarDay} /> Applied month</span>
              <strong>{currentPeriodLabel}</strong>
            </div>
            <div className="opsInfoCard">
              <span><FontAwesomeIcon icon={faCircleInfo} /> Remaining</span>
              <strong>{formatKes(remainingSalary)}</strong>
            </div>
            <div className="opsInfoCard">
              <span><FontAwesomeIcon icon={faBriefcase} /> Role</span>
              <strong>{employee.jobPosition || 'Not set'}</strong>
            </div>
          </div>

          <div className="opsComposer">
            <Form.Group className="mb-3">
              <Form.Label>Salary month</Form.Label>
              <Form.Control type="month" value={periodKey} onChange={(event) => setPeriodKey(event.target.value)} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Amount</Form.Label>
              <Form.Control type="number" name="amount" value={formState.amount} onChange={handleFormChange} placeholder="0" />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Payment date</Form.Label>
              <Form.Control type="datetime-local" name="paymentDate" value={formState.paymentDate} onChange={handleFormChange} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Method</Form.Label>
              <Form.Select name="method" value={formState.method} onChange={handleFormChange}>
                <option value="Bank Transfer">Bank Transfer</option>
                <option value="MPESA">MPESA</option>
                <option value="Cash">Cash</option>
                <option value="Cheque">Cheque</option>
                <option value="Other">Other</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Reference</Form.Label>
              <Form.Control name="reference" value={formState.reference} onChange={handleFormChange} placeholder="Transaction reference" />
            </Form.Group>
            <Form.Group className="mb-0">
              <Form.Label>Notes</Form.Label>
              <Form.Control as="textarea" rows={3} name="notes" value={formState.notes} onChange={handleFormChange} placeholder="Optional payroll note" />
            </Form.Group>

            <p className="opsFormNote">
              You can only pay up to the remaining balance for {currentPeriodLabel}.
            </p>

            <Button variant="dark" className="w-100" onClick={handlePaySalary} disabled={submitting}>
              {submitting ? (
                <>
                  <Spinner as="span" animation="border" size="sm" /> Saving...
                </>
              ) : (
                'Record payment'
              )}
            </Button>
          </div>

          <div className="opsMiniSummary">
            <h6>Contact details</h6>
            <div className="opsMiniSummaryRow">
              <span><FontAwesomeIcon icon={faEnvelope} /> Email</span>
              <strong>{employee.employeeEmail || 'Not set'}</strong>
            </div>
            <div className="opsMiniSummaryRow">
              <span><FontAwesomeIcon icon={faPhone} /> Phone</span>
              <strong>{employee.employeePhone || 'Not set'}</strong>
            </div>
            <div className="opsMiniSummaryRow">
              <span>Personal email</span>
              <strong>{employee.currentEmail || 'Not set'}</strong>
            </div>
          </div>

          <div className="opsActionStack">
            <button type="button" className="opsGhostBtn" onClick={() => copyToClipboard(employee.employeeEmail, 'Email')}>
              Copy work email
            </button>
            <button type="button" className="opsGhostBtn" onClick={() => copyToClipboard(employee.employeePhone, 'Phone')}>
              Copy phone
            </button>
          </div>
        </aside>
      </section>
    </div>
  );
}

export default ViewEmployeeComp;
